import { useState, useEffect, useMemo } from 'react';
import { LuSearch, LuFilter, LuTag } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import TopicItem from './TopicItem';
import { useBuilding } from '../../contexts/BuildingContext';
import { useAuth } from '../../contexts/AuthContext';

export default function TopicList({ scope }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentBuilding, currentProperty } = useBuilding();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('');

  const categories = useMemo(() => [
  { value: 'all', label: t('forum.categories.all') },
  { value: 'general', label: t('forum.categories.general') },
  { value: 'maintenance', label: t('forum.categories.maintenance') },
  { value: 'events', label: t('forum.categories.events') },
  { value: 'rules', label: t('forum.categories.rules') },
  { value: 'suggestions', label: t('forum.categories.suggestions') }],
  [t]);


  useEffect(() => {
    loadTopics();
  }, [scope, currentBuilding, currentProperty, selectedCategory, selectedTag]);

  const loadTopics = async () => {
    setLoading(true);
    try {
      const filters = [
      { name: 'scope', op: 'Equal', value: scope }];


      if (scope === 'building_specific') {
        if (currentBuilding?.id && !currentBuilding.isAll) {
          if (currentBuilding.isPropertyWide) {
            // In forum, property wide scope is explicitly 'property_wide'
            // but if we are in building_specific list and selected 'property wide' filter,
            // it might be better to show nothing or redirect.
            // However, the header building filter usually applies to the data list.
            // For forum, we'll filter by building if not 'All'.
            filters.push({ name: 'building_id', op: 'Equal', value: Number(currentBuilding.id) });
          } else {
            filters.push({ name: 'building_id', op: 'Equal', value: Number(currentBuilding.id) });
          }
        }
      } else if (scope === 'property_wide' && currentProperty?.id) {
        filters.push({ name: 'property_id', op: 'Equal', value: currentProperty.id });
      }

      if (selectedCategory !== 'all') {
        filters.push({ name: 'category', op: 'Equal', value: selectedCategory });
      }
      if (selectedTag) {
        filters.push({ name: 'tags', op: 'StringContains', value: selectedTag });
      }

      const { data, error } = await window.ezsite.apis.tablePage(70652, {
        PageNo: 1,
        PageSize: 50,
        Filters: filters,
        OrderByField: 'is_pinned',
        IsAsc: false
      });

      if (error) throw error;
      let topicList = data?.List || [];

      topicList.sort((a, b) => {
        if (a.is_pinned !== b.is_pinned) {
          return a.is_pinned ? -1 : 1;
        }
        const dateA = new Date(a.last_activity_at || a.created_at);
        const dateB = new Date(b.last_activity_at || b.created_at);
        return dateB - dateA;
      });

      setTopics(topicList);
    } catch (error) {
      console.error('Failed to load topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTopics = topics.filter((topic) =>
  topic.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  topic.body?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('forum.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />

          </div>

          <div className="relative">
            <LuFilter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none">

              {categories.map((cat) =>
              <option key={cat.value} value={cat.value}>{cat.label}</option>
              )}
            </select>
          </div>

          <div className="relative">
            <LuTag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              placeholder={t('forum.tagFilterPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />

          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-200">
        {loading ?
        <div className="p-8 text-center text-gray-500">{t('forum.loadingTopics')}</div> :
        filteredTopics.length === 0 ?
        <div className="p-8 text-center text-gray-500">
            {t('forum.noTopicsFound')}
          </div> :

        filteredTopics.map((topic) =>
        <TopicItem key={topic.id} topic={topic} onUpdate={loadTopics} />
        )
        }
      </div>
    </div>);

}